import { getResultConceptCodes } from './resultConcepts';
import { groupResultRecords, type ObservationRecord, type ResultGroup } from './resultRecords';

/**
 * Clinical categories a lab result is filed under in the "results over time" view.
 *
 * Unlike the immunization grouper's CDC vaccine groups, this table is a **curation, not a
 * transcription**: LOINC publishes no authoritative panel-to-category map, `DiagnosticReport.code`
 * is absent on imports and manual entries, and deriving a category from LOINC parts would put a
 * terminology call in a pure display path. Membership is drawn from the codes curated in
 * `LOINC_LAB_DISPLAY_NAMES`; anything else lands in `other`.
 *
 * Order is the declaration order below — the order a clinician scans a chart in, not recency.
 */
export const LAB_RESULT_CATEGORIES = [
  'diabetes',
  'lipids',
  'kidney',
  'electrolytes',
  'liver',
  'bloodCount',
  'coagulation',
  'thyroid',
  'ironVitamins',
  'inflammation',
  'hormones',
  'cardiac',
  'infectious',
  'pulmonary',
  'other',
] as const;

export type LabResultCategoryKey = (typeof LAB_RESULT_CATEGORIES)[number];

/**
 * The curated codes in each category. `other` has no members — it is where an uncurated code, an
 * uncoded reading and any code deliberately left out of the table land.
 *
 * All three glucose codes are members of `diabetes` even though they are concept siblings: exact
 * match wins in `groupResultRecords`, so fasting, random and meter glucose stay separate rows.
 */
export const LAB_RESULT_CATEGORY_CODES: Record<Exclude<LabResultCategoryKey, 'other'>, readonly string[]> = {
  diabetes: ['4548-4', '1558-6', '2345-7', '2339-0'],
  lipids: ['2093-3', '13457-7', '2085-9', '2571-8', '1884-6', '10835-7', '43583-0'],
  kidney: ['2160-0', '33914-3', '6299-2', '9318-7', '3084-1'],
  electrolytes: ['2951-2', '2823-3', '1963-8', '17861-6', '19123-9', '2777-1', '2731-8'],
  liver: ['1742-6', '1920-8', '6768-6', '1975-2', '1968-7', '1751-7'],
  bloodCount: ['6690-2', '718-7', '4544-3', '777-3', '787-2', '751-8'],
  coagulation: ['5902-2', '6301-6', '14979-9'],
  thyroid: ['3016-3', '3026-2', '3053-6', '8099-4'],
  ironVitamins: ['2498-4', '2276-4', '2500-7', '2132-9', '14635-7'],
  inflammation: ['30522-7', '4537-7', '11572-5', '32218-0'],
  hormones: [
    '2986-8',
    '2991-8',
    '2942-1',
    '2243-4',
    '2839-9',
    '15067-2',
    '10501-5',
    '2842-3',
    '2191-5',
    '21198-7',
    '2857-1',
  ],
  cardiac: ['33762-6', '2157-6'],
  infectious: ['20447-9', '24467-3'],
  pulmonary: ['20150-9', '33452-4'],
};

/**
 * English category names, for the AI context and anywhere else off the React tree. The user-facing
 * labels live in the `records` i18n namespace in `packages/ui`, the same split immunization groups
 * make.
 */
export const LAB_RESULT_CATEGORY_NAMES: Record<LabResultCategoryKey, string> = {
  diabetes: 'Diabetes',
  lipids: 'Lipids',
  kidney: 'Kidney',
  electrolytes: 'Electrolytes & minerals',
  liver: 'Liver',
  bloodCount: 'Blood count',
  coagulation: 'Coagulation',
  thyroid: 'Thyroid',
  ironVitamins: 'Iron & vitamins',
  inflammation: 'Inflammation & autoimmune',
  hormones: 'Hormones & reproductive',
  cardiac: 'Heart & muscle',
  infectious: 'Infectious disease',
  pulmonary: 'Lung function',
  other: 'Other labs',
};

const CATEGORY_BY_CODE: ReadonlyMap<string, LabResultCategoryKey> = new Map(
  LAB_RESULT_CATEGORIES.flatMap((key): [string, LabResultCategoryKey][] =>
    key === 'other' ? [] : LAB_RESULT_CATEGORY_CODES[key].map((code) => [code, key]),
  ),
);

/** Every code the table names, in declaration order — the join key the grouper keys rows on. */
export const LAB_RESULT_CATEGORY_MEMBER_CODES: ReadonlySet<string> = new Set(CATEGORY_BY_CODE.keys());

/**
 * The category a result code belongs to: an exact member first, then a concept sibling of one (so an
 * EHR's `17856-6` files under diabetes alongside the HbA1c it names), then `other`.
 */
export function getLabResultCategory(code: string): LabResultCategoryKey {
  const exact = CATEGORY_BY_CODE.get(code);
  if (exact) return exact;
  for (const sibling of getResultConceptCodes(code)) {
    const viaConcept = CATEGORY_BY_CODE.get(sibling);
    if (viaConcept) return viaConcept;
  }
  return 'other';
}

export type LabResultCategoryGroup = {
  key: LabResultCategoryKey;
  /** One group per lab concept that has readings, newest reading first. */
  groups: ResultGroup[];
  /**
   * Every row the category lists: its curated members in panel order, then any further code that
   * resolved into it. By default a member is listed whether or not it has been drawn — a code with
   * no `ResultGroup` renders as an empty row, because the absence of a lab is itself clinical
   * information. With `GroupLabResultsOptions.includeUndrawn` false, only codes with a `ResultGroup`
   * are listed.
   */
  codes: string[];
  /** The newest reading anywhere in the category; absent when nothing has been drawn. */
  latest?: ObservationRecord;
};

export type GroupedLabResults = {
  /**
   * Every curated category, in declaration order, whether or not the patient has results in it. The
   * list is the same for every patient so it reads as a chart rather than as whatever happens to have
   * been collected. `other` is the exception: it has no curated members, so it appears only once an
   * uncurated or uncoded result exists to put in it.
   */
  categories: LabResultCategoryGroup[];
  /** Administrative rows `groupResultRecords` filed away. */
  hidden: ObservationRecord[];
};

export type GroupLabResultsOptions = {
  /**
   * Whether to list curated categories and member codes that have no readings. `true` by default —
   * the full chart, gaps included. Pass `false` when `records` is a filtered subset (a search, an
   * ownership or status filter): an empty row would then read as "never drawn" when the lab was simply
   * filtered out, so only categories and codes with a matching reading are listed.
   */
  includeUndrawn?: boolean;
};

/**
 * Bucket readings into categories, one row per lab concept within each.
 *
 * Grouping runs against the full member list so a sibling-coded reading snaps onto the member code
 * exactly as it does on the care plan; a text-keyed (uncoded) group falls to `other`.
 */
export function groupLabResultsByCategory(
  records: readonly ObservationRecord[],
  { includeUndrawn = true }: GroupLabResultsOptions = {},
): GroupedLabResults {
  const { groups, hidden } = groupResultRecords(records, LAB_RESULT_CATEGORY_MEMBER_CODES);

  const byCategory = new Map<LabResultCategoryKey, ResultGroup[]>();
  for (const group of groups) {
    const key = getLabResultCategory(group.key);
    const existing = byCategory.get(key);
    if (existing) existing.push(group);
    else byCategory.set(key, [group]);
  }

  const categories = LAB_RESULT_CATEGORIES.flatMap((key) => {
    const categoryGroups = byCategory.get(key) ?? [];
    const allMembers: readonly string[] = key === 'other' ? [] : LAB_RESULT_CATEGORY_CODES[key];
    const drawn = new Set(categoryGroups.map((group) => group.key));
    const members = includeUndrawn ? allMembers : allMembers.filter((code) => drawn.has(code));
    // `other` has no members, so it is the one category that can be empty — and when empty there is
    // nothing to head. With undrawn rows dropped, any category can be.
    if (members.length === 0 && categoryGroups.length === 0) return [];
    // Members first, in panel order, then anything else that landed here (uncurated codes and
    // free-text rows, which only reach `other`).
    const extra = categoryGroups.map((group) => group.key).filter((code) => !members.includes(code));
    return [{ key, groups: categoryGroups, codes: [...members, ...extra], latest: categoryGroups[0]?.latest }];
  });

  return { categories, hidden };
}
